import { inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { filter } from 'rxjs/operators';
import { LoggingService } from './logging.service';
import { PushNotificationService } from './push-notification.service';

export interface DeepLinkEvent {
  url: string;
  path: string;
  queryParams: Record<string, string>;
}

@Injectable({
  providedIn: 'root',
})
export class NativeNavigationService {
  private readonly router = inject(Router);
  private readonly loggingService = inject(LoggingService);
  private readonly pushNotificationService = inject(PushNotificationService);

  private initialized = false;

  private navigationHistory: string[] = [];

  private readonly MAX_HISTORY_SIZE = 50;

  private readonly _lastDeepLink = signal<DeepLinkEvent | null>(null);
  readonly lastDeepLink = this._lastDeepLink.asReadonly();

  private readonly _isInForeground = signal(true);
  readonly isInForeground = this._isInForeground.asReadonly();

  initialize(): void {
    if (this.initialized || !Capacitor.isNativePlatform()) {
      return;
    }

    this.initialized = true;

    this.setupNavigationTracking();

    this.setupBackButtonHandler();

    this.setupDeepLinkHandler();

    this.setupAppStateListener();

    this.checkInitialDeepLink();
  }

  private setupNavigationTracking(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = event.urlAfterRedirects;
        if (this.navigationHistory[this.navigationHistory.length - 1] !== url) {
          this.navigationHistory.push(url);

          if (this.navigationHistory.length > this.MAX_HISTORY_SIZE) {
            this.navigationHistory = this.navigationHistory.slice(-this.MAX_HISTORY_SIZE);
          }
        }
      });
  }

  private setupBackButtonHandler(): void {
    App.addListener('backButton', () => {
      this.handleBackButton();
    });
  }

  private handleBackButton(): void {
    if (this.navigationHistory.length > 1) {
      this.navigationHistory.pop();

      const previousUrl = this.navigationHistory[this.navigationHistory.length - 1];

      if (previousUrl) {
        this.router.navigateByUrl(previousUrl);
        return;
      }
    }

    const currentUrl = this.router.url;
    const isAtRoot = currentUrl === '/' || currentUrl === '/dashboard';

    if (isAtRoot) {
      App.exitApp();
    } else {
      this.navigationHistory = [];
      this.router.navigate(['/dashboard']);
    }
  }

  private setupDeepLinkHandler(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.handleDeepLink(event.url);
    });
  }

  private async checkInitialDeepLink(): Promise<void> {
    try {
      const launchUrl = await App.getLaunchUrl();
      if (launchUrl?.url) {
        this.handleDeepLink(launchUrl.url);
      }
    } catch (error) {
      this.loggingService.logException(error, 'Error checking initial deep link:');
    }
  }

  private handleDeepLink(url: string): void {
    // OAuth redirects (login and step-up MFA) are consumed by the generic-oauth2
    // plugin. Treating them as deep links navigates the app to '/' and tears down
    // whatever view triggered the flow, e.g. the MFA preferences modal.
    if (this.isOAuthRedirect(url)) {
      return;
    }

    const deepLinkEvent = this.parseDeepLink(url);
    this._lastDeepLink.set(deepLinkEvent);

    if (deepLinkEvent.path) {
      this.router.navigate([deepLinkEvent.path], {
        queryParams: deepLinkEvent.queryParams,
      });
    }
  }

  private isOAuthRedirect(url: string): boolean {
    if (url.startsWith('loandepot://') || url.startsWith('http')) {
      return false;
    }

    const scheme = url.split('://')[0];
    const remainder = url.slice(scheme.length + 3);
    const host = remainder.split(/[/?#]/)[0];

    return scheme.startsWith('msauth.') || host === 'oauth' || host === 'auth';
  }

  private parseDeepLink(url: string): DeepLinkEvent {
    try {
      let parsedUrl: URL;

      if (url.startsWith('loandepot://')) {
        const pathPart = url.replace('loandepot://', '');
        parsedUrl = new URL(`https://app.loandepot.com/${pathPart}`);
      } else {
        parsedUrl = new URL(url);
      }

      let path = parsedUrl.pathname;
      if (path.startsWith('/')) {
        path = path.substring(1);
      }

      const queryParams: Record<string, string> = {};
      parsedUrl.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });

      return {
        url,
        path: `/${path}`,
        queryParams,
      };
    } catch (error) {
      this.loggingService.logException(error, 'Error parsing deep link:');
      return {
        url,
        path: '/',
        queryParams: {},
      };
    }
  }

  private setupAppStateListener(): void {
    App.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
      this._isInForeground.set(isActive);

      if (isActive) {
        this.onAppForeground();
      } else {
        this.onAppBackground();
      }
    });
  }

  private onAppForeground(): void {
    this.pushNotificationService.clearNotifications();
  }

  private onAppBackground(): void {
    // Can be extended to save state, pause timers, etc.
  }

  goBack(): void {
    this.handleBackButton();
  }

  clearHistory(): void {
    this.navigationHistory = [];
  }

  getHistory(): string[] {
    return [...this.navigationHistory];
  }
}
